import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import { API } from "@/config";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Bell, Check, Clock, AlertCircle, Send, MessageCircle, Mail } from "lucide-react";

/**
 * NotificationStatusBadge - Shows notification delivery status for an entity
 * @param {string} token - Auth token
 * @param {string} entityType - "appointment" or "invoice"
 * @param {string} entityId - ID of the entity
 * @param {boolean} compact - If true, shows just an icon
 */
export default function NotificationStatusBadge({ token, entityType, entityId, compact = false }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await axios.get(
                    `${API}/notifications/status/${entityType}/${entityId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setStatus(res.data);
            } catch {
                console.log("No notification status found");
                setStatus(null);
            } finally {
                setLoading(false);
            }
        };

        if (entityType && entityId && token) {
            fetchStatus();
        } else {
            setLoading(false);
        }
    }, [token, entityType, entityId]);

    if (loading) {
        return (
            <Badge variant="secondary" className="animate-pulse">
                <Bell className="h-3 w-3 mr-1" />
                ...
            </Badge>
        );
    }

    if (!status || !status.has_notifications) {
        if (compact) return null;
        return (
            <Badge variant="outline" className="text-muted-foreground">
                <Bell className="h-3 w-3 mr-1" />
                No notifications
            </Badge>
        );
    }

    const statusConfig = {
        none: { icon: Bell, color: "bg-slate-100 text-slate-600", label: "None" },
        pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
        sent: { icon: Send, color: "bg-blue-100 text-blue-700", label: "Sent" },
        delivered: { icon: Check, color: "bg-green-100 text-green-700", label: "Delivered" },
        partially_failed: { icon: AlertCircle, color: "bg-orange-100 text-orange-700", label: "Partial" },
        failed: { icon: AlertCircle, color: "bg-red-100 text-red-700", label: "Failed" }
    };

    const config = statusConfig[status.overall_status] || statusConfig.none;
    const Icon = config.icon;

    // Build tooltip content
    const tooltipContent = (
        <div className="space-y-2 text-xs">
            <div className="font-medium">Notification Status: {config.label}</div>
            {status.notifications?.map((notif, idx) => (
                <div key={idx} className="border-t pt-1">
                    <div className="text-muted-foreground">{notif.event_type.replace(/_/g, ' ')}</div>
                    {notif.channels?.map((ch, cidx) => (
                        <div key={cidx} className="flex items-center gap-1 pl-2">
                            {ch.channel === "whatsapp" ? (
                                <MessageCircle className="h-3 w-3 text-green-500" />
                            ) : (
                                <Mail className="h-3 w-3 text-blue-500" />
                            )}
                            <span className="capitalize">{ch.status}</span>
                            {ch.delivered_at && (
                                <span className="text-muted-foreground ml-1">
                                    ({new Date(ch.delivered_at).toLocaleTimeString()})
                                </span>
                            )}
                            {ch.error_message && (
                                <span className="text-red-500 ml-1">- {ch.error_message}</span>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );

    if (compact) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${config.color}`}>
                            <Icon className="h-3 w-3" />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                        {tooltipContent}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Badge className={`cursor-help ${config.color}`}>
                        <Icon className="h-3 w-3 mr-1" />
                        {config.label}
                    </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                    {tooltipContent}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

NotificationStatusBadge.propTypes = {
    token: PropTypes.string,
    entityType: PropTypes.string,
    entityId: PropTypes.string,
    compact: PropTypes.bool
};
